import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import { IconX } from "@tabler/icons-react";

import { studentPlanSelect } from "../services/students";
import { plansList } from "../services/plans";

import {
  formatPaymentType,
  formatPrice,
  formatRecurrence,
} from "../helpers/format";

import Loading from "./Loading";
import { Input } from "./Input";
import { checkCupom } from "../services/coupons";
import moment from "moment";

const StudentPlanSelect = ({
  open,
  onClose,
  id,
  onAction,
  planId,
}: {
  open: boolean;
  onClose: () => void;
  id: string | number;
  onAction: () => void;
  planId?: string | number;
}) => {
  const today = moment().startOf("day");
  const [plan, setPlan] = useState<any>(null);
  const [value, setValue] = useState("1");
  const [discountCode, setDiscountCode] = useState("");
  const [cupomInfo, setCupomInfo] = useState<any>(null);
  const [dueDate, setDueDate] = useState(today.format("YYYY-MM-DD"));

  const { mutate, isPending } = useMutation({
    mutationFn: studentPlanSelect,
    onSuccess: () => {
      toast.success("Plano atualizado para o aluno");
      onAction();
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(
        e?.response?.data?.message ??
          "Erro ao atribuir plano. Tente mais tarde."
      );
      onClose();
    },
  });

  const { mutate: mutateDiscount, isPending: isPendingDiscount } = useMutation({
    mutationFn: async () =>
      await checkCupom({
        discountCode,
        planId: plan?.id,
      }),
    onSuccess: (e: any) => {
      setCupomInfo(e);
    },
    onError: (e: any) => {
      setDiscountCode("");
      toast.error(
        e?.response?.data?.message || "Cupom de desconto não disponivel "
      );
    },
  });

  const {
    data,
    isLoading,
    isFetching,
  }: {
    data: any;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["plansList"],
    queryFn: async () => await plansList({}),
    initialData: [],
  });

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    if (newValue === "1") {
      setDiscountCode("");
      setCupomInfo(null);
    }

    setValue(newValue);
  };

  useEffect(() => {
    if (planId) {
      const currentPlan = data?.find((i: any) => i.id === Number(planId));
      setPlan(currentPlan);
    }
  }, [planId, data]);

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={() => onClose()}>
      <Loading
        loading={isPending || isLoading || isFetching || isPendingDiscount}
      />
      <DialogContent sx={{ backgroundColor: "secondary.main" }}>
        <Grid2 container spacing={1}>
          <Grid2 size={{ xs: 10, md: 11 }}>
            <Typography variant="h2" fontSize={20} fontWeight={600}>
              Selecione um plano para o aluno
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2, md: 1 }}>
            <IconButton onClick={() => onClose()}>
              <IconX style={styles.iconStyle} />
            </IconButton>
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                marginBottom: 2,
              }}
              onChange={handleChangeValue}
              value={value}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Plano" value="1" />
              <Tab label="Desconto" value="2" disabled={Number(value) < 2} />
              <Tab label="Vencimento" value="3" disabled={Number(value) < 3} />
            </Tabs>
          </Grid2>

          {value === "1" &&
            data.map((i: any) => (
              <Grid2
                key={i?.id}
                size={12}
                onClick={() => setPlan(i)}
                sx={{
                  paddingY: 1,
                  paddingX: 2,
                  borderRadius: 2,
                  backgroundColor:
                    plan?.id === i?.id ? "primary.main" : "secondary.main",
                  cursor: "pointer",
                  border: "1px solid #4d4d4d",
                }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  {i?.name}
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={600} fontSize={12}>
                    {i?.description}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} fontSize={12}>
                    Formas de pagamento:{" "}
                    {i?.paymentsType
                      ?.split(",")
                      ?.map((t: string) => `${formatPaymentType(t)} | `)}
                  </Typography>
                  <Typography variant="body1" fontWeight={600} fontSize={12}>
                    {formatPaymentType(i?.paymentType)}
                  </Typography>
                </Stack>
                <Stack
                  spacing={1}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {i?.value ? formatPrice(i?.value) : "Gratís"}
                  </Typography>
                  <Typography variant="body1" fontWeight={600} fontSize={12}>
                    {formatRecurrence(i?.recurrence)}
                  </Typography>
                </Stack>
              </Grid2>
            ))}

          {value === "2" && (
            <Grid2 container alignItems={"flex-end"} size={12}>
              <Grid2 size={9}>
                <Input
                  id="cupom"
                  name="cupom"
                  label="Cupom de desconto"
                  type="text"
                  fullWidth
                  size="small"
                  value={discountCode}
                  onChange={(e: any) => {
                    setDiscountCode(e.target.value.toUpperCase());
                  }}
                />
              </Grid2>
              <Grid2 size={3}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  color={cupomInfo ? "error" : "primary"}
                  onClick={() => {
                    if (cupomInfo) {
                      setDiscountCode("");
                      setCupomInfo(null);
                    } else {
                      mutateDiscount();
                    }
                  }}
                >
                  {cupomInfo ? "Remover" : "Validar"}
                </Button>
              </Grid2>
              <Grid2 size={12} mt={2}>
                <Typography fontWeight={700}>Resumo</Typography>
              </Grid2>

              <Stack sx={{ gap: 1 }} width={"100%"}>
                <Stack justifyContent={"space-between"} direction={"row"}>
                  <Typography color="textSecondary">Valor do plano</Typography>
                  <Typography>{formatPrice(plan?.value || 0)}</Typography>
                </Stack>
                <Stack justifyContent={"space-between"} direction={"row"}>
                  <Typography color="textSecondary">Desconto</Typography>
                  <Typography>
                    - {formatPrice(cupomInfo?.discount || 0)}
                  </Typography>
                </Stack>

                <Stack justifyContent={"space-between"} direction={"row"}>
                  <Typography fontWeight={700}>Total</Typography>
                  <Typography color={"success"} fontWeight={700}>
                    {cupomInfo
                      ? formatPrice(cupomInfo?.newValue || 0)
                      : formatPrice(plan?.value || 0)}
                  </Typography>
                </Stack>
              </Stack>
            </Grid2>
          )}
          {value === "3" && (
            <Grid2 size={12}>
              <Grid2 container>
                <Grid2 size={12}>
                  <Typography>Escolha a data da primeira fatura</Typography>
                </Grid2>
                <Grid2 size={12} sx={{ mt: 2 }}>
                  <Input
                    id="dueDate"
                    name="dueDate"
                    label="Data da primeira fatura"
                    type="date"
                    fullWidth
                    value={dueDate}
                    onChange={(e) => {
                      setDueDate(e?.target?.value);
                    }}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.showPicker()
                    }
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          )}
        </Grid2>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "secondary.main" }}>
        <Stack flexDirection={"row"} gap={1} p={1}>
          <Button
            variant="contained"
            disabled={!plan?.id}
            onClick={() => {
              if (value === "1") {
                if (!plan) {
                  toast.info("Selecione um plano");
                  return;
                }

                setValue("2");
                return;
              }

              if (value === "2") {
                setValue("3");
                return;
              }

              if (value === "3") {
                if (!dueDate) {
                  toast.info("Selecione a data de vencimento");
                  return;
                }

                if (moment(dueDate).isBefore(today)) {
                  toast.error(
                    "A nova data não pode ser anterior ao dia de hoje."
                  );
                  return;
                }

                mutate({
                  studentId: id,
                  planId: plan?.id,
                  discountCode: cupomInfo?.code,
                  dueDate: moment(dueDate).toDate(),
                });
                return;
              }
            }}
          >
            {value === "3" ? "Atribuir plano ao estudante" : "Avançar"}{" "}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default StudentPlanSelect;
